// src/utils/visitTimeline.js
//
// Builds the Visits tab: this doctor's SIGNED visits for the patient, each
// joined with its signed prescription(s) (prescription.visitId === visit.id),
// the vitals reading taken at that visit (reading.visitId === visit.id, else
// reading.appointmentId === visit.appointmentId) and the appointment (for the
// date and type). Pure: no React, no network.

import { localDay } from './consultVitals';
import { addDays, isSigned, itemStatus, fmtDayShort, medicineLabel } from './activeMedicines';
import { newestFirst } from './vitalsTrend';
import { humanise } from './appointmentDisplay';

export const HEADLINE_MAX = 80;

/** First non-blank line of a diagnosis, whitespace-squashed, at most 80 characters (ellipsis included). */
export function diagnosisHeadline(text, max = HEADLINE_MAX) {
  const first = String(text || '').split(/\r?\n/).map(l => l.replace(/\s+/g, ' ').trim()).find(Boolean) || '';
  if (first.length <= max) return first;
  const cut = first.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,;:.\-–—]+$/, '')}…`;
}

export const isSignedVisit = v => String(v?.status || '').toUpperCase() === 'SIGNED';

/** Matches a visit to its vitals reading: by visitId first, then by appointmentId. Latest wins. */
export function readingForVisit(visit, readings) {
  const list = newestFirst(readings);
  return list.find(r => r.visitId && r.visitId === visit.id)
    || list.find(r => visit.appointmentId && r.appointmentId && r.appointmentId === visit.appointmentId)
    || null;
}

const typeLabel = appt => {
  const t = String(appt?.appointmentType || '').toUpperCase();
  if (t === 'FOLLOW_UP') return 'Follow-up';
  if (t === 'WALK_IN') return 'Walk-in';
  return appt?.appointmentType ? humanise(appt.appointmentType) : 'Visit';
};

/** Day the follow-up falls due: signed day + weeks, or null. */
export function followUpDay(visit) {
  const weeks = Number(visit?.followUpWeeks);
  const signed = localDay(visit?.signedAt);
  if (!signed || !Number.isFinite(weeks) || weeks <= 0) return null;
  return addDays(signed, weeks * 7);
}

const sortItems = rx => (rx.items || []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

function prescriptionBlock(rx, today) {
  return {
    id: rx.id, rxNumber: rx.rxNumber || null, signedAt: rx.signedAt || null,
    items: sortItems(rx).map(item => ({ ...item, label: medicineLabel(item), status: itemStatus(rx, item, today) })),
  };
}

/**
 * @param {{visits, prescriptions, vitals, appointments}} data  any may be undefined/[]
 * @param {Date|string} today
 * @returns {{rows: object[], orphanCount: number}}
 *   Rows newest first. Each visit row: {kind:'visit', id, visitId, appointmentId, day, type, headline,
 *   diagnosisText, vitals, rx[], medicineCount, followUpDay, advice, nextSteps, pdfRxId}.
 *   A signed prescription whose visitId matches no signed visit becomes a {kind:'prescription'} row
 *   (so its PDF stays reachable) and is counted in orphanCount. DRAFT visits/prescriptions never appear.
 */
export function buildTimeline({ visits, prescriptions, vitals, appointments } = {}, today = new Date()) {
  const signedVisits = (Array.isArray(visits) ? visits : []).filter(isSignedVisit);
  const signedRx = (Array.isArray(prescriptions) ? prescriptions : []).filter(isSigned);
  const apptById = new Map((Array.isArray(appointments) ? appointments : []).map(a => [a.id, a]));
  const visitIds = new Set(signedVisits.map(v => v.id));

  const rows = signedVisits.map(v => {
    const appt = apptById.get(v.appointmentId);
    const rx = signedRx.filter(r => r.visitId === v.id).map(r => prescriptionBlock(r, today));
    const medicineCount = rx.reduce((n, r) => n + r.items.length, 0);
    const day = (appt?.appointmentDate && localDay(appt.appointmentDate)) || localDay(v.signedAt) || localDay(v.createdAt);
    return {
      kind: 'visit', id: v.id, visitId: v.id, appointmentId: v.appointmentId || null, day,
      appointmentDate: appt?.appointmentDate || null,
      sortKey: String(v.signedAt || v.createdAt || day || ''),
      type: typeLabel(appt), headline: diagnosisHeadline(v.diagnosisText), diagnosisText: v.diagnosisText || '',
      vitals: readingForVisit(v, vitals), rx, medicineCount,
      followUpDay: followUpDay(v), advice: (v.adviceForPatient || '').trim(),
      nextSteps: (v.nextSteps || []).filter(s => s && s.description), signedAt: v.signedAt || null,
      pdfRxId: rx.length ? rx[0].id : null,
    };
  });

  const orphans = signedRx.filter(r => !r.visitId || !visitIds.has(r.visitId));
  orphans.forEach(r => {
    const block = prescriptionBlock(r, today);
    rows.push({
      kind: 'prescription', id: `rx-${r.id}`, visitId: null, appointmentId: null, day: localDay(r.signedAt),
      sortKey: String(r.signedAt || ''), type: 'Prescription only', headline: diagnosisHeadline(r.diagnosisText),
      diagnosisText: r.diagnosisText || '', vitals: null, rx: [block], medicineCount: block.items.length,
      followUpDay: null, advice: (r.advice || '').trim(), nextSteps: [], signedAt: r.signedAt || null, pdfRxId: r.id,
    });
  });

  rows.sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  return { rows, orphanCount: orphans.length };
}

/** Case-insensitive match over diagnosis (headline + full text) and medicine names. Blank query keeps all. */
export function filterTimeline(rows, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return rows;
  return rows.filter(r => [r.headline, r.diagnosisText, ...r.rx.flatMap(x => x.items.map(i => i.label))]
    .some(s => String(s || '').toLowerCase().includes(q)));
}

/** The timeline row a reading belongs to (visitId, else appointmentId), or null. */
export function rowForReading(reading, rows) {
  if (!reading) return null;
  return rows.find(r => r.kind === 'visit' && ((reading.visitId && r.visitId === reading.visitId)
    || (reading.appointmentId && r.appointmentId === reading.appointmentId))) || null;
}

/** The latest signed visit: {id, day, headline}, or null. */
export function lastSignedVisit(visits, appointments) {
  const { rows } = buildTimeline({ visits, appointments, prescriptions: [] });
  const row = rows.find(r => r.kind === 'visit');
  return row ? { id: row.id, day: row.day, headline: row.headline } : null;
}

export { fmtDayShort };
